import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18n/LanguageProvider';

const SITE_URL = 'https://bikmin.tech';
const EN_URL = `${SITE_URL}/`;
const AR_URL = `${SITE_URL}/ar/`;
const PREVIEW_IMAGE_URL = `${SITE_URL}/bikmin-search-preview.png`;
const PREVIEW_IMAGE_WIDTH = '6250';
const PREVIEW_IMAGE_HEIGHT = '6250';

const EN_TITLE = 'Software Company Egypt | Custom Software & AI Development in Cairo | Bikmin';
const EN_DESCRIPTION =
  'Bikmin is a Cairo-based software and AI development company delivering custom software, mobile app development, dashboard and automation systems for Egypt and GCC businesses.';
const EN_KEYWORDS =
  'software company Egypt, custom software, AI development company, mobile app development Cairo, dashboard and automation systems, GCC software services';

const AR_TITLE = 'شركة برمجيات مصر | تطوير برمجيات مخصصة وذكاء اصطناعي في القاهرة | Bikmin';
const AR_DESCRIPTION =
  'بيكمين شركة برمجيات وذكاء اصطناعي في القاهرة تقدم تطوير برمجيات مخصصة، تطوير تطبيقات ومواقع، حلول أنظمة أتمتة ولوحات تحكم للشركات في مصر والخليج.';
const AR_KEYWORDS =
  'شركة برمجيات مصر, تطوير برمجيات مخصصة, شركة ذكاء اصطناعي, تطوير تطبيقات ومواقع, حلول أنظمة أتمتة, خدمات سوفت وير الخليج';

const ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

const getIsArabicRoute = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const path = window.location.pathname.toLowerCase();
  return path === '/ar' || path.startsWith('/ar/');
};

const SeoHead: React.FC = () => {
  const { language } = useLanguage();
  const isArabic = language === 'ar' || getIsArabicRoute();

  const canonical = isArabic ? AR_URL : EN_URL;
  const title = isArabic ? AR_TITLE : EN_TITLE;
  const description = isArabic ? AR_DESCRIPTION : EN_DESCRIPTION;
  const keywords = isArabic ? AR_KEYWORDS : EN_KEYWORDS;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Bikmin Software Solutions',
    alternateName: 'بيكمين للحلول البرمجية',
    url: SITE_URL,
    logo: `${SITE_URL}/icon-512.png`,
    image: PREVIEW_IMAGE_URL,
    email: 'nzmfix4@gmail.com',
    telephone: '+201204246538',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Cairo',
      addressCountry: 'EG',
    },
    areaServed: ['Egypt', 'GCC'],
    availableLanguage: ['en', 'ar'],
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#localbusiness`,
    name: 'Bikmin Software Solutions',
    alternateName: 'بيكمين للحلول البرمجية',
    url: canonical,
    image: PREVIEW_IMAGE_URL,
    description,
    email: 'nzmfix4@gmail.com',
    telephone: '+201204246538',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Cairo',
      addressCountry: 'EG',
    },
    areaServed: ['Egypt', 'GCC'],
    inLanguage: isArabic ? 'ar-EG' : 'en-US',
  };

  return (
    <Helmet>
      <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={ROBOTS} />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={EN_URL} />
      <link rel="alternate" hrefLang="ar" href={AR_URL} />
      <link rel="alternate" hrefLang="x-default" href={EN_URL} />

      <meta property="og:locale" content={isArabic ? 'ar_EG' : 'en_US'} />
      <meta property="og:locale:alternate" content={isArabic ? 'en_US' : 'ar_EG'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={PREVIEW_IMAGE_URL} />
      <meta property="og:image:width" content={PREVIEW_IMAGE_WIDTH} />
      <meta property="og:image:height" content={PREVIEW_IMAGE_HEIGHT} />
      <meta property="og:image:alt" content="Bikmin Software Solutions" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={PREVIEW_IMAGE_URL} />
      <meta name="twitter:image:alt" content="Bikmin Software Solutions" />

      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
    </Helmet>
  );
};

export default SeoHead;
